import type {WidgetType} from "constants/WidgetConstants";
import * as React from "react";
import type {WidgetProps, WidgetState} from "widgets/BaseWidget";
import TaroWidget from "widgetsTaro";
import {ValidationTypes} from "constants/WidgetValidation";
import type {Stylesheet} from "entities/AppTheming";
import {DefaultAutocompleteDefinitions} from "widgets/WidgetUtils";
import type {AutocompletionDefinitions} from "widgets/constants";
import TInputWidget from "../component";
import {DerivedPropertiesMap} from "../../../utils/WidgetFactory";
import {FontStyleTypes} from "constants/WidgetConstants";
import {fontSizeOptions} from "../../../pages/Editor/ThemePropertyPane/controls/ThemeFormControl";
import {isAutoLayout} from "../../../utils/autoLayout/flexWidgetUtils";
import {LabelPosition} from "../../../components/constants";
import {Alignment} from "@blueprintjs/core";
import type {BaseInputWidgetProps} from "../../../widgets/BaseInputWidget/widget";
import contentConfig from "./contentConfig";
import styleConfig from "./styleConfig";
import {EventType} from "../../../constants/AppsmithActionConstants/ActionConstants";

class TInput extends TaroWidget<TInputWidgetProps, WidgetState> {
  constructor(props: TInputWidgetProps) {
    super(props);
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      value: "string",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
    };
  }

  static getPropertyPaneEventsConfig() {
    return super.getWidgetEvents("AtInputWidget");
  }

  static getPropertyPaneContentConfig = contentConfig;
  static getPropertyPaneStyleConfig = styleConfig;

  static getStylesheetConfig(): Stylesheet {
    return {
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      boxShadow: "none",
      fontSize: "{{appsmith.theme.form.label.fontSize}}",
      color: "{{appsmith.theme.form.label.fontColor}}",
      fontStyle: "{{appsmith.theme.form.label.fontStyle}}",
      inputFontSize: "{{appsmith.theme.form.inputText.fontSize}}",
      inputColor: "{{appsmith.theme.form.inputText.fontColor}}",
      inputFontStyle: "{{appsmith.theme.form.inputText.fontStyle}}",
      bgColor: "{{appsmith.theme.form.inputBorder.bgColor}}",
      borderColor: "{{appsmith.theme.form.inputBorder.bgBorderColor}}",
    };
  }

  static getMetaPropertiesMap(): Record<string, any> {
    return {
      isDirty: false,
      value: ''
    };
  }
  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      getValue: `{{ this.value }}`,
      isValid: `{{ this.isRequired ? !!this.value : true }}`,
    };
  }
  static getDefaultPropertiesMap(): Record<string, string> {
    return {
      value: "defaultValue"
    };
  }

  onValueChange = (updatedValue: string) => {
    this.props.updateWidgetMetaProperty("value", updatedValue);
    if(this.props.onChange){
      super.executeAction({
        triggerPropertyName: "onChange",
        dynamicString: this.props.onChange,
        event: {
          type: EventType.ON_CHANGE,
        },
      });
    }
  };
  onValueBlur = () => {
    if(this.props.onBlur){
      super.executeAction({
        triggerPropertyName: "onBlur",
        dynamicString: this.props.onBlur,
        event: {
          type: EventType.ON_BLUR,
        },
      });
    }
  };
  onValueFocus = () => {
    if(this.props.onFocus){
      super.executeAction({
        triggerPropertyName: "onFocus",
        dynamicString: this.props.onFocus,
        event: {
          type: EventType.ON_FOCUS,
        },
      });
    }
  };

  getPageView() {
    return (
      <TInputWidget
        {...this.props}
        onValueBlur={this.onValueBlur}
        onValueFocus={this.onValueFocus}
        onValueChange={this.onValueChange}
      />
    );
  }


  static getWidgetType(): WidgetType {
    return "AT_INPUT_WIDGET";
  }
}

export interface TInputWidgetProps extends WidgetProps {
  value: any[];
  enableDownload?: boolean;
  enableRotation?: boolean;
  onClick?: string;
  borderRadius: string;
  boxShadow?: string;
}

export default TInput;
